// src/api/categories.ts
import axios from 'api/helpers/axios';
import type { AxiosResponse, AxiosError } from 'axios';
import {
  ICategory,
  ICategoryItem,
  IPaginationResponse,
  IPartialCategoryItem,
  ListType
} from 'types/types';
import { toSlug } from 'utilities/functions';

interface ICategoriesListResponse {
  data: IPaginationResponse<ICategory> | ListType<ICategory>;
  status: number;
  statusText: string;
}

export async function fetchCategories(
  params?: Record<string, unknown>
): Promise<AxiosResponse<ICategoriesListResponse>> {
  return axios.get<ICategoriesListResponse>('/product-categories', { params });
}

interface ICategoryResponse<P> {
  data: P;
  status?: number;
  statusText?: string;
  message?: string;
  name?: string;
}

interface IAddCategory {
  name: string;
  parent_id?: number;
}

/** Обрезаем *_url поля рекурсивно (без continue) */
function stripCategoryUrlFields<T>(obj: T): T {
  if (Array.isArray(obj)) {
    const mapped = obj.map((it) => stripCategoryUrlFields(it)) as unknown as T;
    return mapped;
  }
  if (obj && typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    const entries = Object.entries(obj as Record<string, unknown>);
    for (let i = 0; i < entries.length; i += 1) {
      const pair = entries[i];
      const key = pair[0];
      const value = pair[1];

      if (/_url$/.test(key)) {
        // пропускаем такие ключи
      } else {
        out[key] = stripCategoryUrlFields(value);
      }
    }
    return out as T;
  }
  return obj;
}

const DROP_KEYS = new Set(['parent_name', 'main', 'extra1', 'extra2']);

/** Санитизация перед сохранением (без continue и без тернарников) */
function sanitizeCategoryForSave<T>(obj: T): T {
  if (Array.isArray(obj)) {
    const mapped = obj.map((it) => sanitizeCategoryForSave(it)) as unknown as T;
    return mapped;
  }
  if (obj && typeof obj === 'object') {
    const src = obj as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    const keys = Object.keys(src);

    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const value = src[key];

      const isUrlField = /_url$/.test(key);
      const isDropped = DROP_KEYS.has(key);
      const isUndefined = typeof value === 'undefined';

      if (!isUrlField && !isDropped && !isUndefined) {
        out[key] = sanitizeCategoryForSave(value as never);
      }
    }

    return out as T;
  }
  return obj;
}

export type SlotName = 'icon' | 'extra1' | 'extra2';

export interface UploadSlotResponse {
  ok: boolean;
  url: string;
  slot: string;
}

export interface RemoveSlotResponse {
  ok: boolean;
}

export interface RequestOptions {
  signal?: AbortSignal;
  onUploadProgress?: (e: ProgressEvent) => void;
}

export default {
  async getCategoriesList(
    page?: number,
    paginate?: boolean,
    perpage?: number,
    wherein?: string
  ): Promise<ICategoriesListResponse> {
    const params: Record<string, unknown> = {};
    if (typeof wherein === 'string' && wherein.length > 0) {
      params.wherein = wherein;
    } else {
      if (typeof page !== 'undefined') params.page = page;
      if (typeof paginate !== 'undefined') params.paginate = paginate;
      if (typeof perpage !== 'undefined') params.perpage = perpage;
    }
    const response = await axios.get('/categories', { params });
    return response;
  },

  async getCategory(
    categoryId: number
  ): Promise<ICategoryResponse<ICategoryItem>> {
    const response = await axios.get(`/categories/${categoryId}`);
    return response;
  },

  async addCategory(data: IAddCategory): Promise<ICategoryResponse<unknown>> {
    const payload = sanitizeCategoryForSave({
      ...data,
      slug: toSlug(data)
    } as unknown as Record<string, unknown>);
    const response = await axios.post('/categories', payload);
    return response;
  },

  async editCategory(
    id: number,
    data: ICategory | ICategoryItem
  ): Promise<ICategoryResponse<unknown>> {
    const candidateSlug = (data as ICategoryItem).slug;
    let slugValue = candidateSlug;
    if (!slugValue) {
      slugValue = toSlug(data);
    }
    const payload = sanitizeCategoryForSave({
      ...data,
      slug: slugValue
    } as unknown as Record<string, unknown>);
    const response = await axios.patch(`/categories/${id}`, payload);
    return response;
  },

  async patchCategory(
    id: number,
    data: IPartialCategoryItem
  ): Promise<ICategoryResponse<unknown>> {
    const candidateSlug = (data as ICategoryItem).slug;
    let slugValue = candidateSlug;
    if (!slugValue) {
      slugValue = toSlug(data);
    }
    const payload = sanitizeCategoryForSave({
      ...data,
      slug: slugValue
    } as unknown as Record<string, unknown>);
    const response = await axios.patch(`/categories/${id}`, payload);
    return response;
  },

  async deleteCategory(id: number): Promise<ICategoryResponse<unknown>> {
    const response = await axios.delete(`/categories/${id}`);
    return response;
  },

  // ====== Медиа-слоты категории ======

  async uploadSlot(
    categoryId: number,
    slot: SlotName,
    file: File,
    opts?: RequestOptions
  ): Promise<AxiosResponse<UploadSlotResponse>> {
    const fd = new FormData();
    fd.append('file', file);

    const config: Record<string, unknown> = {
      headers: { 'Content-Type': 'multipart/form-data' }
    };
    if (opts && opts.signal) config.signal = opts.signal;
    if (opts && opts.onUploadProgress)
      config.onUploadProgress = opts.onUploadProgress;

    return axios.post<UploadSlotResponse>(
      `/categories/${categoryId}/media/${slot}`,
      fd,
      config
    );
    // eslint-disable-next-line no-unreachable
  },

  async uploadSlotSafe(
    categoryId: number,
    slot: SlotName,
    file: File,
    opts?: RequestOptions
  ): Promise<
    | { ok: true; url: string; slot: string }
    | { ok: false; message: string; status?: number }
  > {
    try {
      const resp = await this.uploadSlot(categoryId, slot, file, opts);
      return { ok: true, url: resp.data.url, slot: resp.data.slot };
    } catch (err) {
      const e = err as AxiosError<any>;
      let { message } = e;
      let status: number | undefined;
      if (e.response) {
        status = e.response.status;
        if (e.response.data && e.response.data.message) {
          message = e.response.data.message;
        }
      }
      return { ok: false, message, status };
    }
  },

  async removeSlot(
    categoryId: number,
    slot: SlotName,
    opts?: RequestOptions
  ): Promise<AxiosResponse<RemoveSlotResponse>> {
    const config: Record<string, unknown> = {};
    if (opts && opts.signal) config.signal = opts.signal;
    return axios.delete<RemoveSlotResponse>(
      `/categories/${categoryId}/media/${slot}`,
      config
    );
  },

  async removeSlotSafe(
    categoryId: number,
    slot: SlotName,
    opts?: RequestOptions
  ): Promise<{ ok: true } | { ok: false; message: string; status?: number }> {
    try {
      await this.removeSlot(categoryId, slot, opts);
      return { ok: true };
    } catch (err) {
      const e = err as AxiosError<any>;
      let { message } = e;
      let status: number | undefined;
      if (e.response) {
        status = e.response.status;
        if (e.response.data && e.response.data.message) {
          message = e.response.data.message;
        }
      }
      return { ok: false, message, status };
    }
  }
};
